import { useCallback } from "react";
import { apiClient } from "module/api";
import { updateCampaignExtraData } from "graphQL/mutations/campaignExtraData";
import { useCampaignData } from "campaign-builder/context/CampaignDataContext";

// Shared helper for the tracking-invalidation flow (F5). Tracking is "tied to the
// design/audience" when it depends on a variable file or per-recipient placeholders
// (unique promo, or any linked variable) — static codes/URLs survive design and
// audience changes, so they don't trigger the warning.
export const useTrackingInvalidation = () => {
  const { campaignId, campaignData, campaignVariables, refetchCampaign, refetchCampaignVariables } = useCampaignData();

  const hasPlaceholderTracking = campaignData?.couponType === "variable" || (campaignVariables?.length ?? 0) > 0;

  const resetTracking = useCallback(async () => {
    if (!campaignId) return;
    // Clear the coupon/QR config first, then unlink variables. Errors propagate (not
    // swallowed) so the caller can abort the design/audience change instead of leaving
    // a half-reset state (e.g. couponType still "variable" but variables unlinked).
    // The refetches are awaited so hasPlaceholderTracking isn't stale on return.
    await updateCampaignExtraData({
      campaignId,
      isTrackableCoupon: 0,
      couponType: null,
      staticCouponCode: null,
      qrCodeLink: null,
    });
    await Promise.all(
      (campaignVariables ?? []).map((cv) => {
        const variableId = cv?.variable?.id ?? cv?.variable_id;
        return variableId ? apiClient.unlinkVariableWithCampaign(campaignId, Number(variableId)) : Promise.resolve();
      })
    );
    await refetchCampaignVariables?.();
    await refetchCampaign?.();
  }, [campaignId, campaignVariables, refetchCampaign, refetchCampaignVariables]);

  return { hasPlaceholderTracking, resetTracking };
};
